import { useState, useCallback, useEffect } from 'react';
import { useNavigationManager } from '@provider/navigation';
import { useNotifier } from '@provider/notifier';
import { executeIntegrationCallbacks, mapPollyVoice } from '@provider/speech-synthesis';
import { findNearestScroller, throttleToAnimationFrame, isScrolling, stopScrolling, loadPollyLibrary, scrollTo } from '@utils/common';
import { configRefs } from '@utils/constants';
import { useLoaderState, useSelf } from '@utils/react';

const ASSUMED_HEADER_HEIGHT_PX = 100;
const MAX_AUTO_SCROLL_THRESHOLD_PX = 200;
const MAX_AUTO_SCROLL_DISTANCE_PX = 150;
let AUTO_INCREMENTED_READER_ID = 0;
/**
 * provide highlevel methods for integrating the gPolly speech synthesis
 * library and it's built-in karaoke features.
 */
function usePolly({
  highlightCls,
  highlightTransitionMs,
  scrollContainerRef,
  enabled,
  speed,
  gender,
  latexPrinter,
  karaoke,
  initialLanguage,
  rootSelector
}) {
  const notifier = useNotifier();
  const navigation = useNavigationManager();
  const [, setLanguage] = useState(initialLanguage);
  const [isLoading, setLoading] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const [isReading, setReading] = useState(false);
  const [progress, setProgress] = useState(0);
  const showLoader = useLoaderState(isLoading);
  const self = useSelf({
    setLoading,
    setPlaying,
    setReading,
    setProgress,
    notifier
  });
  const startPolly = useCallback((rangeOrAudioElement, language) => {
    self.readerId = ++AUTO_INCREMENTED_READER_ID;
    setLanguage(language);
    const resolvedScrollContainerRef = (() => {
      if (scrollContainerRef) {
        return scrollContainerRef;
      }
      const nearestScroller = findNearestScroller(document.querySelector(rootSelector));
      if (nearestScroller) {
        return {
          current: nearestScroller
        };
      }
      return undefined;
    })();
    const state = {
      language,
      speed,
      gender: gender ?? "female",
      karaoke,
      latexPrinter,
      showLoader,
      scrollContainerRef: resolvedScrollContainerRef,
      highlightCls,
      highlightTransitionMs
    };
    const getRangeOrAudioElement = typeof rangeOrAudioElement === "function" ? rangeOrAudioElement : createNoopAfterFirstCall(() => rangeOrAudioElement);
    readOutLoud({
      self,
      state,
      readerId: self.readerId,
      getRangeOrAudioElement
    });
  }, [gender, highlightCls, highlightTransitionMs, karaoke, latexPrinter, rootSelector, scrollContainerRef, self, showLoader, speed]);
  const pausePolly = useCallback(() => {
    if (self.reader) {
      self.reader.pause();
      setPlaying(false);
    }
  }, [self]);
  const resumePolly = useCallback(() => {
    if (self.reader) {
      self.reader.resume();
      setPlaying(true);
    }
  }, [self]);
  const stopPolly = useCallback(() => {
    if (self.reader) {
      self.reader.stop();
    }
    self.readerId = undefined;
  }, [self]);
  const changePollyLanguage = useCallback(language => {
    setLanguage(prevLanguage => {
      if (prevLanguage !== language) {
        self.reader?.stop();
      }
      return language;
    });
  }, [self]);

  // auto-stop speech synthesis if user changes page while we're reading out
  // load
  useEffect(() => {
    return navigation.subscribe(() => {
      self.reader?.stop();
    });
  }, [self, navigation]);

  // auto-stop speech synthesis if speed or gender is changed
  useEffect(() => {
    self.reader?.stop();
  }, [self, speed, gender]);

  // auto-stop speech synthesis if component is unmounted from the DOM
  useEffect(() => {
    return () => self.reader?.stop();
  }, [self]);
  return {
    isUnavailable: !enabled,
    isReading,
    isPlaying,
    isLoading,
    showLoader: showLoader.current,
    progress,
    startPolly,
    pausePolly,
    resumePolly,
    stopPolly,
    changePollyLanguage
  };
}

/**
 * helper that'll start the polly speech synthesis behind the scenes.
 */
async function readOutLoud({
  self,
  state,
  readerId,
  getRangeOrAudioElement
}) {
  self.setLoading(true);

  // bind a scroll listener to auto-pause polly if user scrolls it out of view
  // if karaoke features is enabled
  const handleScroll = throttleToAnimationFrame(() => onScroll(self, state));
  window.addEventListener("scroll", handleScroll);
  while (true) {
    self.setLoading(true);
    self.setProgress(0);

    // keep iterating over ranges until there are no more of them available
    const rangeOrAudioElement = await getRangeOrAudioElement();
    if (!rangeOrAudioElement) {
      break;
    }
    if (readerId !== self.readerId) {
      // bail out if user changed page while trying to start reading the page
      // out loud
      break;
    }
    let language = state.language;
    let wasCancelled = false;

    // read this range out loud (restart if user changed language while we were
    // reading it)
    do {
      language = state.language;
      self.setLoading(true);
      self.setProgress(0);

      // Stop playback from all other possibly active media to avoid multiple
      // sources playing at the same time.
      for (const mediaPlayer of Array.from(document.body.querySelectorAll("audio, video"))) {
        if (rangeOrAudioElement instanceof HTMLAudioElement && mediaPlayer === rangeOrAudioElement) {
          continue;
        }
        if (!mediaPlayer.paused) {
          mediaPlayer.pause();
        }
      }
      if (rangeOrAudioElement instanceof HTMLAudioElement) {
        await executeIntegrationCallbacks({
          type: "audio",
          element: rangeOrAudioElement
        });
        wasCancelled = await awaitAudioElement(self, rangeOrAudioElement);
      } else {
        await executeIntegrationCallbacks({
          type: "range",
          range: rangeOrAudioElement
        });
        wasCancelled = await readRangeOutLoud(self, state, rangeOrAudioElement);
      }
    } while (readerId === self.readerId && language !== state.language && wasCancelled);
    if (wasCancelled) {
      break;
    }
  }
  self.setLoading(false);
  self.setReading(false);
  self.setPlaying(false);
  window.removeEventListener("scroll", handleScroll);
}
function awaitAudioElement(self, audio) {
  return new Promise(resolve => {
    let progressFrameId = 0;
    const onProgress = () => {
      self.setProgress(audio.currentTime / audio.duration);
      progressFrameId = requestAnimationFrame(onProgress);
    };
    const onStart = () => {
      self.setLoading(false);
      self.setPlaying(true);
      clearTimeout(timeoutId);
      self.reader = {
        pause: () => audio.pause(),
        resume: () => audio.play(),
        stop: () => audio.pause()
      };
      onProgress();
    };
    const onPause = () => {
      self.setPlaying(!audio.paused);
      if (audio.paused) {
        cancelAnimationFrame(progressFrameId);
      }
    };
    const onSuccess = () => {
      resolve(false);
      cancelAnimationFrame(progressFrameId);
      clearTimeout(timeoutId);
      audio.removeEventListener("play", onStart);
      audio.removeEventListener("ended", onSuccess);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("error", onCancel);
      audio.removeEventListener("cancel", onCancel);
    };
    const onCancel = () => {
      resolve(true);
      cancelAnimationFrame(progressFrameId);
      clearTimeout(timeoutId);
      audio.removeEventListener("play", onStart);
      audio.removeEventListener("ended", onSuccess);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("error", onCancel);
      audio.removeEventListener("cancel", onCancel);
    };

    // NB: In case the audio element never resolves, then reset after a while.
    const timeoutId = setTimeout(onCancel, 60_000);
    audio.addEventListener("play", onStart);
    audio.addEventListener("ended", onSuccess);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("error", onCancel);
    audio.addEventListener("cancel", onCancel);
  });
}
function readRangeOutLoud(self, state, range) {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async resolve => {
    try {
      const gPolly = await loadPollyLibrary();
      self.reader = gPolly.readOutLoud({
        speed: state.speed,
        voice: mapPollyVoice(state.language, state.gender),
        range,
        latexPrinter: state.latexPrinter ?? undefined,
        karaoke: state.karaoke,
        // defer audio playback while a loader is being displayed
        onReady: async play => {
          self.setLoading(false);
          while (state.showLoader.current) {
            await new Promise(resolve => setTimeout(resolve, 1));
          }
          return play();
        },
        // track progress while reading out loud, so we can properly update
        // button state
        onStart: () => {
          self.setPlaying(true);
          self.setReading(true);
          self.setProgress(0);
        },
        onProgress: progress => {
          self.setProgress(progress);
        },
        onEnd: () => {
          resolve(false);
        },
        onError: () => {
          resolve(false);
        },
        onCancel: () => {
          resolve(true);
        },
        // perform fade in/out of karaoke highlight elements
        onKaraokeHighlight: node => {
          node.classList.add(state.highlightCls);
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          node.offsetWidth;
          node.classList.add("enter");

          // scroll the currently highlighted word into view, if it isn't
          // currently visible
          const bbox = node.getBoundingClientRect();

          // The autoScrolling threshold should not be too large on small
          // viewports since this could trigger scrolling down in one frame and
          // then scrolling upwards in the next since the checks below would
          // 'overlap'. Hence we limit the autoscroll by a ratio of the height
          // of the window.
          const ratio = Math.min(MAX_AUTO_SCROLL_THRESHOLD_PX, window.innerHeight * 0.25) / MAX_AUTO_SCROLL_THRESHOLD_PX;
          const autoScrollThresholdPx = ratio * MAX_AUTO_SCROLL_THRESHOLD_PX;
          const autoScrollDistancePx = ratio * MAX_AUTO_SCROLL_DISTANCE_PX;
          const scrollContainerBBox = state.scrollContainerRef?.current?.getBoundingClientRect();
          const scrollOffsetPx = {
            x: state.scrollContainerRef?.current ? state.scrollContainerRef.current.scrollLeft : window.scrollX,
            y: state.scrollContainerRef?.current ? state.scrollContainerRef.current.scrollTop : window.scrollY
          };
          const scrollContainerOffsetPx = {
            x: scrollContainerBBox?.left ?? 0,
            y: scrollContainerBBox?.top ?? 0
          };
          const scrollHeightPx = {
            x: state.scrollContainerRef?.current ? state.scrollContainerRef.current.clientWidth : window.innerWidth,
            y: state.scrollContainerRef?.current ? state.scrollContainerRef.current.clientHeight : window.innerHeight
          };
          const headerHeightPx = state.scrollContainerRef?.current ? 0 : ASSUMED_HEADER_HEIGHT_PX;
          if (bbox.top > scrollContainerOffsetPx.y + scrollHeightPx.y - autoScrollThresholdPx) {
            scrollTo(bbox.top - scrollContainerOffsetPx.y + scrollOffsetPx.y - scrollHeightPx.y + autoScrollThresholdPx + autoScrollDistancePx, {
              scrollContainer: state.scrollContainerRef?.current ?? undefined
            });
          } else if (bbox.top < scrollContainerOffsetPx.y + autoScrollThresholdPx + headerHeightPx) {
            scrollTo(bbox.top - scrollContainerOffsetPx.y + scrollOffsetPx.y - autoScrollThresholdPx - headerHeightPx - autoScrollDistancePx, {
              scrollContainer: state.scrollContainerRef?.current ?? undefined
            });
          }
          if (bbox.left > scrollContainerOffsetPx.x + scrollHeightPx.x - autoScrollThresholdPx) {
            const scrollTo = bbox.left - scrollContainerOffsetPx.x + scrollOffsetPx.x - scrollHeightPx.x + autoScrollThresholdPx + autoScrollDistancePx;
            (state.scrollContainerRef?.current ?? window).scrollTo({
              left: scrollTo,
              behavior: "auto"
            });
          } else if (bbox.left < scrollContainerOffsetPx.x + autoScrollThresholdPx + headerHeightPx) {
            const scrollTo = bbox.left - scrollContainerOffsetPx.x + scrollOffsetPx.x - autoScrollThresholdPx - autoScrollDistancePx;
            (state.scrollContainerRef?.current ?? window).scrollTo({
              left: scrollTo,
              behavior: "auto"
            });
          }
        },
        onKaraokeNodeHide: node => {
          node.classList.add("exit");
          return new Promise(resolve => window.setTimeout(resolve, state.highlightTransitionMs));
        },
        enableNodeValidation: configRefs.env.app.dangerouslyAssertSyncValue() !== "production",
        onNodeValidationFailed: () => {
          self.notifier.notify({
            id: "polly.karaoke.violating-nodes",
            type: "error",
            body: "Nogle elementer på siden er ikke kompatible med gPolly karaoke-funktionen. Dette bør rettes."
          });
        }
      });
    } catch (err) {
      self.notifier.notify({
        id: "polly.read-out-loud.error",
        type: "error",
        body: "Noget gik galt da vi forsøgte at starte oplæsningen. Prøv igen senere ved at genindlæse siden."
      });
      console.error(err);
      resolve(true);
    }
  });
}

/**
 * listener that's triggered if user scrolls, and automatically pauses the
 * speech synthesis if the user scrolls the read words out of view.
 */
function onScroll(self, state) {
  if (isScrolling()) {
    // ignore scroll events triggered by the scrollTo utility
    return;
  }
  if (!self.reader) {
    return;
  }
  const highlightNode = document.querySelector(`.${state.highlightCls}:not(.exit)`);
  if (highlightNode && isOutOfViewport(highlightNode)) {
    self.reader?.pause();
    self.setPlaying(false);
    stopScrolling();
  }
}
function isOutOfViewport(node) {
  const bbox = node.getBoundingClientRect();
  return bbox.bottom < 0 || bbox.top > window.innerHeight - ASSUMED_HEADER_HEIGHT_PX;
}

/**
 * utility to create a wrapper around a callback that's transformed into a noop
 * after the first execution.
 */
function createNoopAfterFirstCall(cb) {
  let hasBeenCalled = false;
  return (...args) => {
    if (!hasBeenCalled) {
      hasBeenCalled = true;
      return cb(...args);
    }
  };
}

export { usePolly };
//# sourceMappingURL=hook.polly.js.map
